import { observer } from 'mobx-react-lite';

import { Info } from 'components/base/Info';
import { DetailCard } from 'components/details/DetailCard';
import { DetailList } from 'components/details/DetailList';
import { EditBoolean } from 'components/forms/EditBoolean';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { EditEventPathReference } from 'edit/reference/EditEventPathReference';
import { EditSchedulingWindowReference, type SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import { type AutoSubmitSelection, type EditableWaitEvent } from '../../edit/EditableWaitEvent';

const AUTO_SUBMIT_OPTIONS: EditSelectOptionList<AutoSubmitSelection> = [
  { key: 'off', value: 'off', label: 'No auto-submit' },
  { key: 'if_none_pending', value: 'if_none_pending', label: 'Submit if none pending' },
  { key: 'always', value: 'always', label: 'Always submit' },
];

// The wait event stores its scheduling window as a `zone|name` path; EditSchedulingWindowReference works in {zone_name, name}.
const splitSchedulingWindow = (value: string | null): SchedulingWindowReference | null => {
  if (!value) return null;
  const [zone_name = '', name = ''] = value.split('|');
  return { zone_name, name };
};
const joinSchedulingWindow = (sw: SchedulingWindowReference | null): string | null => (sw ? `${sw.zone_name}|${sw.name}` : null);

// Its own observer so a field edit re-renders just this card (and the collapsed subtitle), not the whole list.
const WaitEventCard = observer(({ event }: { readonly event: EditableWaitEvent }) => (
  <DetailCard title="Wait Event" subtitle={event.summary} collapsible onRemove={event.delete} removeLabel="Remove wait event">
    <div className="d-flex flex-column gap-2">
      <EditEventPathReference label="Event" value={event.path} onChange={(v) => (event.path = v)} />
      <div className="d-flex gap-3">
        <div className="flex-fill">
          {/* Auto-submit only takes effect when a default queue is set on the Properties tab; the tab's info banner says so.
              We don't disable or rewrite the field for a missing queue — the server is the validation authority. */}
          <EditSelect<AutoSubmitSelection>
            label="Auto Submit"
            value={event.autoSubmitSelection}
            onChange={(v) => (event.autoSubmitSelection = v)}
            options={AUTO_SUBMIT_OPTIONS}
          />
        </div>
        {event.autoSubmitSelection !== 'off' && (
          <div className="flex-fill">
            <EditTimeZoneReference
              label="Submit Timezone"
              value={event.autoSubmitTimezone}
              onChange={(v) => (event.autoSubmitTimezone = v)}
              placeholder="use the configured default timezone"
            />
          </div>
        )}
        <div className="d-flex align-items-center">
          <EditBoolean label="Auto Clear" value={event.autoClear} onChange={(v) => (event.autoClear = v)} />
        </div>
      </div>
      <div className="d-flex gap-3">
        <div className="flex-fill">
          <EditSchedulingWindowReference
            label="Scheduling Window"
            value={splitSchedulingWindow(event.schedulingWindow)}
            onChange={(sw) => (event.schedulingWindow = joinSchedulingWindow(sw))}
          />
        </div>
        <div className="flex-fill">
          <EditString label="Precondition (true or =expression)" value={event.precondition} onChange={(v) => (event.precondition = v)} />
        </div>
      </div>
    </div>
  </DetailCard>
));

/**
 * The Wait Events tab: the definition's wait-event links, a native MobX view over its Aggregate (add / remove / edit). Cards
 * aren't reorderable — order isn't meaningful for events.
 */
export const WaitEventsTab = observer(({ entity }: { readonly entity: EditableNodeDefinition }) => (
  <div className="d-flex flex-column gap-3">
    <Info heading="Auto-submit needs a default queue.">
      A wait event can only auto-submit into the default queue set for this node. Assign one on the Properties tab to enable it.
    </Info>
    <DetailList
      items={entity.waitEvents}
      getKey={(event) => event.key}
      onAdd={entity.createWaitEvent}
      addLabel="Add wait event"
      renderItem={(event) => <WaitEventCard event={event} />}
    />
  </div>
));
